import {
  AddBundleAction,
  AddBundlesInput,
  AddBundlesInputBundle,
  CartChannel,
} from '__generated__/useAddToCartMutation.graphql';
import { getSelectedLineNumberDetailsBasedOnPlans } from '../../components/ProductConfigurator/utils/getSelectedLineNumberDetailsBasedOnPlans.utils';
import {
  EligibleLines,
  IfpEligibilityType,
  WearableEligibilityType,
} from '../../typings';

interface BuildBundleParameter {
  itemIds: string[];
  ifpEligibility: IfpEligibilityType;
  wearableEligibility: WearableEligibilityType;
}

interface PlanItems {
  isChangePlan: boolean;
  offerId: string;
}

const getPlanItems = ({ isChangePlan, offerId }: PlanItems) => {
  return {
    offerId,
    quantity: 1,
    action: isChangePlan
      ? ('delete' as AddBundleAction)
      : ('keep' as AddBundleAction),
  };
};

type CartBundleItem = {
  offerId: string;
  quantity: number;
  action: AddBundleAction;
};

interface GetEligibilityBuildBundleParameter {
  isChangePlan: boolean;
  selectedLineService: EligibleLines[0];
  newItems: CartBundleItem[];
}

interface EligibilityBuildBundle {
  lineNumber?: string;
  items: CartBundleItem[];
  categoryId: string;
  accountNumber: string;
}

const getEligibilityBuildBundle = ({
  isChangePlan,
  selectedLineService,
  newItems,
}: GetEligibilityBuildBundleParameter) => {
  const planItems = getPlanItems({
    isChangePlan,
    offerId: selectedLineService.offerId,
  });

  let bundle: EligibilityBuildBundle = {
    categoryId: 'mobile',
    accountNumber: selectedLineService.accountNumber,
    items: newItems,
  };

  if (selectedLineService.lineNumber) {
    bundle = {
      ...bundle,
      lineNumber: selectedLineService.lineNumber,
      items: [...newItems, planItems],
    };
  }

  return bundle;
};

function buildBundle({
  itemIds,
  ifpEligibility,
  wearableEligibility,
}: BuildBundleParameter): AddBundlesInputBundle {
  const newItems = itemIds.map((id) => ({
    offerId: id,
    quantity: 1,
    action: 'add' as AddBundleAction,
  }));
  const selectedLineDetails =
    getSelectedLineNumberDetailsBasedOnPlans(ifpEligibility);
  if (ifpEligibility?.isCheckRequired && selectedLineDetails) {
    return getEligibilityBuildBundle({
      selectedLineService: selectedLineDetails,
      isChangePlan: ifpEligibility?.isChangePlan,
      newItems,
    });
  }

  if (wearableEligibility.isCheckRequired && wearableEligibility.eligibility) {
    const { eligibility, selectedLineNumber, selectedAccountNumber } =
      wearableEligibility;
    let selectedLineService;
    if (selectedLineNumber) {
      selectedLineService = [
        ...eligibility?.eligibleLines,
        ...eligibility?.ineligibleLines,
      ].find((line) => line.lineNumber === selectedLineNumber);
    } else {
      selectedLineService = [...eligibility?.eligibleLines].find(
        (line) => line.accountNumber === selectedAccountNumber,
      );
    }

    return getEligibilityBuildBundle({
      isChangePlan: false,
      selectedLineService,
      newItems,
    });
  }
  return {
    categoryId: 'mobile',
    items: newItems,
    ...(ifpEligibility?.selectedAccountNumber && {
      accountNumber: ifpEligibility.selectedAccountNumber,
    }),
  };
}

interface BuildAddBundleInputParameter {
  itemIds: string[];
  ifpEligibility: IfpEligibilityType;
  wearableEligibility: WearableEligibilityType;
  channel: CartChannel;
}

export function buildAddBundleInput({
  channel,
  itemIds,
  ifpEligibility,
  wearableEligibility,
}: BuildAddBundleInputParameter): AddBundlesInput {
  return {
    bundles: [buildBundle({ ifpEligibility, wearableEligibility, itemIds })],
    channel,
    processContext: 'NEW_OFFER',
  };
}
